import { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { ArrowLeft, History, Loader2, Pencil, Trash2 } from "lucide-react";
import { Button } from "@/frontend/components/ui/button";
import { CopyField } from "@/frontend/components/ui/copy-field";
import { Separator } from "@/frontend/components/ui/separator";
import { orpc } from "@/frontend/orpc-client";
import { cn } from "@/frontend/lib/utils";
import { STATUS_TABS } from "@/frontend/types/dashboard/settings/activity";
import { formatTimeAgo, initialFormDrafts, loadEditDestinationDraft } from "@/frontend/utils/dashboard/settings/export";
import { getDestinationLogo, getDestTypeLabel } from "@/frontend/utils/dashboard/settings/activity";
import { useExportEvents } from "@/frontend/hooks/useExportEvents";
import { DESTINATION_TYPE } from "@/server/types/export";
import { RunningBanner } from "./RunningBanner";
import { HistoryRow } from "./HistoryRow";
import { AddDestinationDialog } from "./AddDestinationDialog";
import type { DestinationDialogType, FormDrafts } from "@/frontend/types/dashboard/settings/export";

/**
 * Detail view for a single export destination.
 *
 * 1. Fetches the destination from the cached destinations list
 * 2. Renders a header with destination info and edit/delete/toggle actions
 * 3. Fetches export runs scoped to this destination
 * 4. Renders status filter tabs, running banners, and history rows
 *
 * @param destinationId - The ID of the destination to display
 */
export function DestinationDetailView({ destinationId }: { destinationId: string }) {
  useExportEvents();

  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const destinations = useQuery(orpc.admin.listExportDestinations.queryOptions({ input: {} }));
  const destination = (destinations.data ?? []).find((d: any) => d.id === destinationId);
  const shouldShowInternalFhirDetails =
    destination?.destination_type === DESTINATION_TYPE.FHIR_SERVER && destination.is_internal === true;
  const internalDestinationDetails = useQuery({
    ...orpc.admin.getExportDestination.queryOptions({
      input: {
        destinationId,
      },
    }),
    enabled: shouldShowInternalFhirDetails,
  });

  const runs = useQuery({
    ...orpc.admin.listExportRuns.queryOptions({
      input: {
        destinationId,
        limit: 50,
        offset: 0,
      },
    }),
  });

  const [filterStatus, setFilterStatus] = useState<string>("all");

  const allRuns = runs.data ?? [];

  const filteredRuns = useMemo(() => {
    return allRuns.filter((run: any) => {
      return filterStatus === "all" || run.status === filterStatus;
    });
  }, [allRuns, filterStatus]);

  const statusCounts = useMemo(() => {
    const counts: Record<string, number> = { all: allRuns.length };
    for (const tab of STATUS_TABS) {
      if (tab.key !== "all") {
        counts[tab.key] = allRuns.filter((r: any) => r.status === tab.key).length;
      }
    }
    return counts;
  }, [allRuns]);

  const runningRuns = filteredRuns
    .filter((r: any) => r.status === "running" || r.status === "queued")
    .sort((a: any, b: any) => (a.status === "running" ? -1 : 1) - (b.status === "running" ? -1 : 1));
  const historyRuns = filteredRuns.filter((r: any) => r.status !== "running" && r.status !== "queued");

  // --- Edit dialog state (same pattern as DestinationsSection) ---
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogType, setDialogType] = useState<DestinationDialogType | null>(null);
  const [formDrafts, setFormDrafts] = useState<FormDrafts>(initialFormDrafts);

  const toggleMutation = useMutation({
    ...orpc.admin.toggleExportDestination.mutationOptions(),
    onSuccess: () => void destinations.refetch(),
  });
  const deleteMutation = useMutation({
    ...orpc.admin.deleteExportDestination.mutationOptions(),
    onSuccess: () => {
      void destinations.refetch();
      void navigate({
        to: "/dashboard/settings",
        search: { section: "destinations", destinationId: undefined, provider: "" },
      });
    },
  });

  /** Opens the edit dialog pre-filled with the destination's current values (secrets included). */
  const openEditDialog = async () => {
    if (!destination) return;
    const { type, drafts } = await loadEditDestinationDraft(queryClient, orpc, destination.id);
    setDialogType(type);
    setFormDrafts((prev) => ({ ...prev, ...drafts }));
    setDialogOpen(true);
  };

  /** Resets the dialog form drafts and closes the dialog. */
  const clearDraftAndClose = () => {
    if (dialogType) {
      setFormDrafts((prev) => ({
        ...prev,
        [dialogType]: initialFormDrafts()[dialogType],
      }));
    }
    setDialogOpen(false);
    void queryClient.invalidateQueries({ queryKey: orpc.admin.listExportDestinations.key() });
    void queryClient.invalidateQueries({
      queryKey: orpc.admin.getExportDestination.key({ input: { destinationId } }),
    });
  };

  // Loading state
  if (destinations.isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="size-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // Destination not found
  if (!destination) {
    return (
      <div className="flex flex-col gap-4">
        <Link
          to="/dashboard/settings"
          search={{ section: "destinations", destinationId: undefined, provider: "" }}
          className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors w-fit"
        >
          <ArrowLeft className="size-4" />
          Back to Export
        </Link>
        <p className="text-sm text-muted-foreground">Destination not found.</p>
      </div>
    );
  }

  const isActive = destination.enabled;
  const typeLabel = getDestTypeLabel(destination);

  return (
    <div className="flex flex-col gap-8">
      {/* Back link */}
      <Link
        to="/dashboard/settings"
        search={{ section: "destinations", destinationId: undefined, provider: "" }}
        className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors w-fit"
      >
        <ArrowLeft className="size-4" />
        Back to Export
      </Link>

      {/* Destination header */}
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-4">
          <img
            src={getDestinationLogo(destination)}
            alt={typeLabel}
            className="w-8 h-8 object-contain shrink-0"
          />
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-xl font-semibold">{destination.name}</h2>
              <span
                className={cn(
                  "inline-flex items-center text-xs px-2 py-0.5 rounded-full font-medium capitalize",
                  isActive
                    ? "bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400"
                    : "bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400"
                )}
              >
                {isActive ? "Active" : "Inactive"}
              </span>
            </div>
            <p className="text-sm text-muted-foreground mt-0.5">
              {typeLabel}
              {destination.lastRunCompletedAt &&
                ` · Last export: ${formatTimeAgo(destination.lastRunCompletedAt)}`}
            </p>
          </div>
        </div>
        <div className="flex gap-1">
          <Button
            size="sm"
            variant="ghost"
            className="h-8 px-3 text-xs"
            onClick={() =>
              toggleMutation.mutate({
                destinationId: destination.id,
                enabled: !isActive,
              })
            }
            disabled={toggleMutation.isPending}
          >
            {isActive ? "Disable" : "Enable"}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            className="h-8 w-8 p-0"
            onClick={openEditDialog}
          >
            <Pencil className="size-4" />
          </Button>
          <Button
            size="sm"
            variant="ghost"
            className="h-8 w-8 p-0 text-destructive hover:text-destructive hover:bg-destructive/10"
            onClick={() =>
              deleteMutation.mutate({ destinationId: destination.id })
            }
            disabled={deleteMutation.isPending}
          >
            <Trash2 className="size-4" />
          </Button>
        </div>
      </div>

      <Separator />

      {shouldShowInternalFhirDetails && internalDestinationDetails.data && (
        <div className="rounded-lg border bg-card p-4">
          <h3 className="text-sm font-semibold mb-4">Internal FHIR Server</h3>
          <div className="grid gap-4 md:grid-cols-2">
            <CopyField label="FHIR Base URL" value={internalDestinationDetails.data.fhir_server_url ?? ""} />
            <CopyField label="Bearer Token" value={internalDestinationDetails.data.bearer_token ?? ""} masked />
          </div>
        </div>
      )}

      {shouldShowInternalFhirDetails && internalDestinationDetails.data && <Separator />}

      {/* Export History */}
      <div>
        <h3 className="text-sm font-semibold mb-5">Export History</h3>

        {/* Status filter tabs */}
        <div className="flex gap-1 mb-6">
          {STATUS_TABS.map(({ key, label }) => (
            <button
              key={key}
              onClick={() => setFilterStatus(key)}
              className={cn(
                "px-2 py-1 text-xs font-medium rounded transition-colors",
                filterStatus === key
                  ? "bg-primary text-primary-foreground"
                  : "text-muted-foreground hover:bg-muted"
              )}
            >
              {label} ({statusCounts[key] ?? 0})
            </button>
          ))}
        </div>

        {runs.isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="size-6 animate-spin text-muted-foreground" />
          </div>
        ) : filteredRuns.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
            <History className="h-8 w-8 mb-2 opacity-50" />
            <p className="text-sm">No export runs yet</p>
          </div>
        ) : (
          <div className="flex flex-col gap-6">
            {/* In Progress */}
            {runningRuns.length > 0 && (
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">
                  In Progress
                </p>
                <div className="flex flex-col gap-3">
                  {runningRuns.map((run: any) => (
                    <RunningBanner key={run.id} run={run} />
                  ))}
                </div>
              </div>
            )}

            {/* History */}
            {historyRuns.length > 0 && (
              <div>
                {runningRuns.length > 0 && (
                  <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">
                    History
                  </p>
                )}
                <div className="rounded-lg border divide-y">
                  {historyRuns.map((run: any) => (
                    <HistoryRow
                      key={run.id}
                      run={run}
                      onRefetch={() => void runs.refetch()}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Edit dialog */}
      {dialogType && (
        <AddDestinationDialog
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          type={dialogType}
          formDrafts={formDrafts}
          setFormDrafts={setFormDrafts}
          onSaveSuccess={clearDraftAndClose}
          destinationId={destinationId}
        />
      )}
    </div>
  );
}
